import { useCallback, useEffect, useState } from "react";
import {
  DndContext, useDraggable, useDroppable, closestCenter,
  PointerSensor, KeyboardSensor, useSensor, useSensors, DragOverlay,
} from "@dnd-kit/core";
import {
  Library as LibIcon, Plus, ChevronDown, ChevronRight, BookOpen, X, Send, Eye, Play, Skull, Trophy,
  UploadCloud, GripVertical, FolderPlus, Loader2, School,
} from "lucide-react";
import {
  getClassLibrary, detachLectureFromClass, startGame, organizeLecture,
} from "@/lib/api";
import { AttachLectureModal } from "./AttachLectureModal";
import { toast } from "sonner";

// Highlighted drop-target for a chapter group header. Highlights when a lecture
// is being dragged over so the instructor sees where the drop will land.
const ChapterDropZone = ({ chapter, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `chapter::${chapter}`, data: { chapter } });
  return (
    <div
      ref={setNodeRef}
      data-testid={`chapter-drop-${chapter}`}
      className={`transition-colors ${isOver ? "bg-accent/10 ring-2 ring-accent/60 rounded-2xl" : ""}`}
    >
      {children}
    </div>
  );
};

// Drop-target for cross-class moves — drag a lecture onto another class chip
// to file it into that class.
const CrossClassChip = ({ cls }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `class::${cls.id}`, data: { moveToClass: cls.id, moveToClassName: cls.name } });
  return (
    <div
      ref={setNodeRef}
      data-testid={`class-drop-${cls.id}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
        isOver ? "border-accent bg-accent/20 scale-105 shadow-md" : "border-border bg-background/60 hover:border-accent/40"
      }`}
    >
      <School className={`h-3 w-3 ${isOver ? "text-accent" : "text-muted-foreground"}`} />
      {cls.name}
    </div>
  );
};

// Draggable lecture row — carries its lecture id + current chapter so the drop
// handler can PATCH the correct fields.
const DraggableLecture = ({ id, chapter, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `lec::${id}`,
    data: { lectureId: id, fromChapter: chapter },
  });
  return (
    <div
      ref={setNodeRef}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className="relative"
    >
      <button
        type="button"
        {...listeners}
        {...attributes}
        aria-label="Drag to move to another chapter"
        title="Drag to another chapter"
        className="absolute -left-1 top-1/2 -translate-y-1/2 grid place-items-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="pl-6">{children}</div>
    </div>
  );
};

// Class Library — instructor-only staging area (hidden from students).
export const ClassLibrary = ({ classroom, onAssignFromLibrary, onPreview, onUpload, otherClasses = [], refreshTick }) => {
  const [data, setData] = useState(null);
  const [openGroup, setOpenGroup] = useState({});
  const [attachOpen, setAttachOpen] = useState(false);
  const [activeDrag, setActiveDrag] = useState(null);
  const [newChapterOpen, setNewChapterOpen] = useState(false);
  const [newChapterName, setNewChapterName] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor));

  // Memoized so the useEffect below can safely depend on `load` without
  // re-running every render. Only rebuilds when the classroom id changes.
  const load = useCallback(() => {
    return getClassLibrary(classroom.id).then((d) => {
      setData(d);
      setOpenGroup((prev) => {
        const next = { ...prev };
        (d?.groups || []).forEach((g) => {
          if (next[g.chapter_label] === undefined) next[g.chapter_label] = true;
        });
        return next;
      });
    }).catch(() => setData({ total: 0, groups: [] }));
  }, [classroom.id]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const detach = async (lid) => {
    if (!window.confirm("Remove this lecture from the class library? (The lecture stays in your own library.)")) return;
    try {
      await detachLectureFromClass(classroom.id, lid);
      toast.success("Removed from class");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  const onDragStart = (evt) => {
    const d = evt.active?.data?.current;
    if (!d?.lectureId) return;
    let title = "Lecture";
    for (const g of data?.groups || []) {
      const l = (g.lectures || []).find((x) => x.id === d.lectureId);
      if (l) { title = l.title; break; }
    }
    setActiveDrag({ ...d, title });
  };

  const onDragEnd = async (evt) => {
    setActiveDrag(null);
    const drop = evt.over?.data?.current;
    const drag = evt.active?.data?.current;
    if (!drag?.lectureId || !drop) return;
    try {
      if (drop.moveToClass) {
        if (drop.moveToClass === classroom.id) return;
        await organizeLecture(drag.lectureId, { classroom_id: drop.moveToClass, chapter_label: "Uploaded" });
        toast.success(`Moved to "${drop.moveToClassName || 'another class'}"`);
        load();
        return;
      }
      if (drop.chapter) {
        if (drop.chapter === drag.fromChapter) return;
        await organizeLecture(drag.lectureId, {
          classroom_id: classroom.id,
          chapter_label: drop.chapter,
        });
        toast.success(`Moved to "${drop.chapter}"`);
        load();
        return;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move");
    }
  };

  const openUpload = () => {
    try {
      sessionStorage.setItem("lectura_pending_upload", JSON.stringify({
        classroom_id: classroom.id,
        chapter_label: "",
        class_name: classroom.name,
      }));
    } catch {}
    if (onUpload) onUpload();
  };

  const createNewChapter = () => {
    const name = newChapterName.trim();
    if (!name) return;
    setOpenGroup((s) => ({ ...s, [name]: true }));
    setNewChapterName("");
    setNewChapterOpen(false);
    setData((d) => {
      if (!d) return d;
      if ((d.groups || []).some((g) => g.chapter_label === name)) return d;
      return { ...d, groups: [{ chapter_label: name, lectures: [] }, ...(d.groups || [])] };
    });
    toast.success(`Chapter "${name}" ready — drag a lecture into it.`);
  };

  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>;

  return (
    <div data-testid="class-library">
      <div className="flex items-center justify-between gap-4 mb-3">
        <h3 className="flex items-center gap-2 font-serif text-xl font-bold">
          <LibIcon className="h-5 w-5" /> Class Library
          <span className="ml-1 rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Instructor-only</span>
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="new-chapter-btn"
            onClick={() => setNewChapterOpen((s) => !s)}
            className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60 transition"
            title="Add an empty chapter so you can drag lectures into it"
          >
            <FolderPlus className="h-4 w-4" /> New chapter
          </button>
          <button
            data-testid="upload-to-class-btn"
            onClick={openUpload}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition"
            title="Upload chapter files (PDF, DOCX, image) — each becomes a lecture in this class"
          >
            <UploadCloud className="h-4 w-4" /> Upload files
          </button>
          <button
            data-testid="attach-lecture-btn"
            onClick={() => setAttachOpen(true)}
            className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60 transition"
          >
            <Plus className="h-4 w-4" /> Attach existing
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground -mt-1 mb-4">
        Hidden from students. Save 45–50 slide lectures here as you build your curriculum. Drag <GripVertical className="inline h-3 w-3" /> to move between chapters. When ready, click <span className="font-semibold">Assign to students</span> to post one as an assignment.
      </p>

      {newChapterOpen && (
        <div className="mb-3 rounded-2xl border border-accent/40 bg-accent/5 p-3 flex items-center gap-2" data-testid="new-chapter-inline">
          <input
            data-testid="new-chapter-input"
            value={newChapterName}
            onChange={(e) => setNewChapterName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createNewChapter()}
            placeholder="Chapter label (e.g. Chp 46 dental impressions)"
            autoFocus
            className="flex-1 min-w-0 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition"
          />
          <button
            type="button"
            data-testid="new-chapter-save"
            onClick={createNewChapter}
            disabled={!newChapterName.trim()}
            className="rounded-lg bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold disabled:opacity-40 hover:opacity-90 transition"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => { setNewChapterOpen(false); setNewChapterName(""); }}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition"
            aria-label="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {data.total === 0 && !newChapterOpen ? (
        <div className="rounded-2xl border border-dashed border-border py-10 px-6 text-center">
          <BookOpen className="h-8 w-8 text-accent mx-auto" />
          <p className="mt-3 font-serif text-lg">No lectures in this class library yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Click <span className="font-semibold">Upload files</span> to drop in chapters — Lectura reads each PDF/DOCX/image and files them here as full lectures, or attach one you've already made.</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        {otherClasses.length > 0 && (
          <div className="mb-4 rounded-2xl border border-dashed border-border bg-secondary/30 px-4 py-3" data-testid="cross-class-strip">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Drag onto another class to move it
            </p>
            <div className="flex flex-wrap gap-2">
              {otherClasses.map((c) => (
                <CrossClassChip key={c.id} cls={c} />
              ))}
            </div>
          </div>
        )}
        <ul className="space-y-3">
          {data.groups.map((g) => (
            <ChapterDropZone key={g.chapter_label} chapter={g.chapter_label}>
            <li data-testid={`chapter-group-${g.chapter_label}`} className="rounded-2xl border border-border bg-card overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenGroup((s) => ({ ...s, [g.chapter_label]: !s[g.chapter_label] }))}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-secondary/40 transition"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {openGroup[g.chapter_label] ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                  <BookOpen className="h-4 w-4 text-accent shrink-0" />
                  <span className="font-serif text-lg font-bold truncate">{g.chapter_label}</span>
                </div>
                <span className="text-xs text-muted-foreground font-mono shrink-0">{g.lectures.length}</span>
              </button>
              {openGroup[g.chapter_label] && (
                (g.lectures.length === 0 ? (
                  <p className="px-6 py-4 text-xs text-muted-foreground border-t border-border italic">Drop a lecture here to file it under this chapter.</p>
                ) : (
                <ul className="divide-y divide-border border-t border-border">
                  {g.lectures.map((l) => (
                    <li key={l.id} data-testid={`lib-lec-${l.id}`} className="px-4 py-3">
                    <DraggableLecture id={l.id} chapter={g.chapter_label}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <button
                        data-testid={`open-${l.id}`}
                        onClick={() => onPreview && onPreview(l.id)}
                        title="Open lecture"
                        className="flex-1 min-w-0 text-left rounded-lg -mx-2 px-2 py-1 hover:bg-secondary/40 transition"
                      >
                        <p className="font-semibold truncate group-hover:text-accent">{l.title}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground truncate">
                          {l.book_title}
                          {l.edition ? ` · ${l.edition}` : ""}
                          {" · "}<span className="uppercase">{l.depth}</span>
                          {" · "}{l.quiz_count} Qs
                          {l.language && l.language !== "English" ? ` · ${l.language}` : ""}
                        </p>
                      </button>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          data-testid={`preview-${l.id}`}
                          onClick={() => onPreview && onPreview(l.id)}
                          title="Preview"
                          className="p-2 rounded-full hover:bg-secondary/60 transition"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`livegame-${l.id}`}
                          onClick={async () => {
                            try {
                              const g = await startGame(l.id, { mode: "classic" });
                              toast.success(`Live game started · Room ${g.code}`);
                              window.open(`/game/host/${g.id}`, "_blank");
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Couldn't start game");
                            }
                          }}
                          title="Start a live class game"
                          className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 text-white hover:bg-purple-700 px-3 py-1.5 text-xs font-semibold transition"
                        >
                          <Play className="h-3.5 w-3.5" /> Live
                        </button>
                        <button
                          data-testid={`royale-${l.id}`}
                          onClick={async () => {
                            if (!window.confirm("Start BATTLE ROYALE mode? One wrong answer = elimination. Last student standing wins.")) return;
                            try {
                              const g = await startGame(l.id, { mode: "royale" });
                              toast.success(`Battle Royale started · Room ${g.code}`);
                              window.open(`/game/host/${g.id}`, "_blank");
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Couldn't start Battle Royale");
                            }
                          }}
                          title="Battle Royale — one wrong answer eliminates you, last standing wins"
                          className="inline-flex items-center gap-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 px-3 py-1.5 text-xs font-semibold transition"
                        >
                          <Skull className="h-3.5 w-3.5" /> Royale
                        </button>
                        <button
                          data-testid={`jeopardy-${l.id}`}
                          onClick={async () => {
                            try {
                              const g = await startGame(l.id, { mode: "jeopardy" });
                              toast.success(`Jeopardy started · Room ${g.code}`);
                              window.open(`/game/host/${g.id}`, "_blank");
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "This lecture needs more concept variety to run Jeopardy.");
                            }
                          }}
                          title="Jeopardy — team-based category board with buzz-in answers"
                          className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 px-3 py-1.5 text-xs font-semibold transition"
                        >
                          <Trophy className="h-3.5 w-3.5" /> Jeopardy
                        </button>
                        <button
                          data-testid={`assign-${l.id}`}
                          onClick={() => onAssignFromLibrary(l)}
                          disabled={l.is_assigned}
                          className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          title={l.is_assigned ? "Already assigned to students" : "Assign this lecture to students"}
                        >
                          <Send className="h-3.5 w-3.5" /> {l.is_assigned ? "Assigned" : "Assign"}
                        </button>
                        <button
                          data-testid={`detach-${l.id}`}
                          onClick={() => detach(l.id)}
                          title="Remove from class"
                          className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    </DraggableLecture>
                    </li>
                  ))}
                </ul>
                ))
              )}
            </li>
            </ChapterDropZone>
          ))}
        </ul>
        <DragOverlay>
          {activeDrag ? (
            <div className="rounded-xl border border-accent bg-card px-4 py-2 shadow-2xl text-sm font-semibold max-w-md">
              📄 {activeDrag.title}
            </div>
          ) : null}
        </DragOverlay>
        </DndContext>
      )}

      <AttachLectureModal open={attachOpen} onClose={() => setAttachOpen(false)} classroom={classroom} onAttached={load} />
    </div>
  );
};
